import { faKey } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useShareboxGetLocations } from 'api/sharebox/sharebox';
import Input from 'components/inputs/Input';
import LabelWrap from 'components/inputs/LabelWrap';
import Select from 'components/inputs/Select';
import Routes from 'constants/Routes';
import useHasAccessToSharebox from 'hooks/useHasAccessToSharebox';
import { FC } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const Wrap = styled(LabelWrap)`
  & > *:last-child {
    width: auto;
  }
`;

const Hint = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.foreground.secondary};
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

type Props = {
  /** register('shareboxLocationID', { valueAsNumber: true }) från formuläret. */
  registration: UseFormRegisterReturn;
  /** Aktuellt värde, för att kunna behålla ett ID som inte finns i listan. */
  value: number | undefined;
  /** Undefined i skapa-läge — då finns ingen hubb att länka till ännu. */
  companyId: number | undefined;
};

/**
 * Kopplingen mellan en adress och ett Sharebox-skåp.
 *
 * Fältet var tidigare ett fritt heltal, vilket gjorde två fel osynliga: en felskrivning
 * upptäcktes först när nycklar aldrig dök upp, och samma ID på två adresser gjorde
 * attributeringen tvetydig. Listan från Sharebox tar bort båda — man kan inte skriva fel,
 * och redan kopplade locationer är märkta.
 *
 * Går listan inte att hämta (API nere, eller användare utanför piloten) faller fältet
 * tillbaka på fritextinmatning. Kopplingen ska aldrig blockeras av att Sharebox är nere.
 */
const ShareboxLocationField: FC<Props> = ({
  registration,
  value,
  companyId,
}) => {
  const hasAccess = useHasAccessToSharebox();
  const locationsCall = useShareboxGetLocations({
    query: { enabled: hasAccess },
  });

  const locations = locationsCall.data ?? [];
  const canPick = locations.length > 0;

  // Ett redan sparat ID som Sharebox inte listar (borttagen location, eller nyckel som
  // inte når den) får inte tyst försvinna ur formuläret när man sparar om adressen.
  const valueMissingFromList =
    value != null &&
    locations.every((l) => l.shareboxLocationID !== value);

  const selected = locations.find((l) => l.shareboxLocationID === value);
  const takenByOther =
    selected?.takenByCompanyID != null &&
    selected.takenByCompanyID !== companyId;

  return (
    <Wrap label="Sharebox location-ID">
      <div>
        {canPick ? (
          <Select {...registration}>
            <option value="">— ingen koppling —</option>
            {valueMissingFromList && (
              <option value={value}>
                {value} (finns inte i Sharebox-listan)
              </option>
            )}
            {locations.map((l) => {
              const taken =
                l.takenByCompanyID != null && l.takenByCompanyID !== companyId;
              return (
                <option
                  key={l.shareboxLocationID}
                  value={l.shareboxLocationID}
                  disabled={taken}
                >
                  {l.name} ({l.shareboxLocationID})
                  {l.city ? ` · ${l.city}` : ''}
                  {l.cabinetCount > 0 ? ` · ${l.cabinetCount} skåp` : ''}
                  {taken ? ` — kopplad till ${l.takenByCompanyName}` : ''}
                </option>
              );
            })}
          </Select>
        ) : (
          <Input type="number" {...registration} />
        )}

        {takenByOther && (
          <Hint>
            Location {value} är redan kopplad till{' '}
            {selected?.takenByCompanyName}. Sparning kommer att nekas.
          </Hint>
        )}

        {value != null && companyId !== undefined && (
          <Hint>
            <FontAwesomeIcon icon={faKey} />
            <Link to={`${Routes.sharebox.index}#hub-${companyId}`}>
              Visa luckorna för den här hubben
            </Link>
          </Hint>
        )}

        {!canPick && hasAccess && !locationsCall.isLoading && (
          <Hint>
            Kunde inte hämta locationer från Sharebox — ange ID:t manuellt.
          </Hint>
        )}
      </div>
    </Wrap>
  );
};

export default ShareboxLocationField;
